import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import { useTheme } from '../context/ThemeContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Briefcase, ExternalLink, ChevronRight, Search, X, Loader2, ZoomIn, ZoomOut, RotateCcw } from 'lucide-react';

// Keep this aligned with NetworkMapView tier semantics.
const PEOPLE_ACCENT = '#2563EB';

const WORK_COLOR = '#34D399';
const EDU_COLOR = '#8B5CF6';
const WORK_TIER_COLORS = ['#10B981', '#22C55E', '#16A34A', '#15803D', '#4ADE80', '#065F46'];
const EDU_TIER_COLORS = ['#8B5CF6', '#A78BFA', '#7C3AED', '#6D28D9', '#5B21B6', '#4C1D95'];
const DEST_WORK_COLOR = '#3B82F6';
const DEST_EDU_COLOR = '#F59E0B';
const TIERS = ['T1', 'T2', 'T3', 'T4', 'T5', 'T6'];
const WORK_TIER_LABELS = {
  T1: 'Same company · Similar role · Same tenure',
  T2: 'Same company · Similar role · ±2 yrs',
  T3: 'Same company · Same tenure',
  T4: 'Same company · ±2 yrs',
  T5: 'Same company · Similar role',
  T6: 'Same company background',
};
const EDU_TIER_LABELS = {
  T1: 'Same college · Same branch · Same batch',
  T2: 'Same college · Same branch · ±2 yrs',
  T3: 'Same college · Same batch · Diff branch',
  T4: 'Same college · ±2 yrs · Diff branch',
  T5: 'Same college · Same branch',
  T6: 'Same college background',
};

const norm = (v) => String(v || '').trim().toLowerCase();

const getLabels = (item) => {
  const tags = Array.isArray(item?.relation_tags) ? item.relation_tags : [];
  if (tags.length > 0) {
    return tags
      .map((t) => (typeof t === 'string' ? t : t?.label || ''))
      .map((t) => String(t || '').trim())
      .filter(Boolean);
  }
  const raw = String(item?.strongest_relation || '').trim();
  if (!raw) return [];
  return raw.split('·').map((x) => String(x || '').trim()).filter(Boolean);
};

const getTagLabels = (c) => {
  const tags = Array.isArray(c?.relation_tags) ? c.relation_tags : [];
  if (tags.length > 0) {
    return tags
      .map((t) => (typeof t === 'string' ? t : t?.label || ''))
      .map((t) => String(t || '').trim())
      .filter(Boolean);
  }

  const raw = String(c?.strongest_relation || '').trim();
  if (!raw) return [];
  return raw.split('·').map((x) => String(x || '').trim()).filter(Boolean);
};

const classifyTier = (labels, scope) => {
  if (scope === 'work') {
    const role = labels.some((l) => /same role|similar role/i.test(l));
    const sameTen = labels.some((l) => /same tenure/i.test(l));
    const nearTen = labels.some((l) => /near tenure|±2 yrs/i.test(l));
    if (role && sameTen) return 'T1';
    if (role && nearTen) return 'T2';
    if (sameTen) return 'T3';
    if (nearTen) return 'T4';
    if (role) return 'T5';
    return 'T6';
  }
  const branch = labels.some((l) => /same branch|similar branch/i.test(l));
  const sameBatch = labels.some((l) => /same batch/i.test(l));
  const nearBatch = labels.some((l) => /near batch|±2 yrs/i.test(l));
  if (branch && sameBatch) return 'T1';
  if (branch && nearBatch) return 'T2';
  if (sameBatch) return 'T3';
  if (nearBatch) return 'T4';
  if (branch) return 'T5';
  return 'T6';
};

function buildGraph({
  workAnchors = [],
  educationAnchors = [],
  centerLabel = 'YOU',
  selectedAnchor = 'you',
  selectedTiers = [],
  workTabsData = {},
  collegeTabsData = {},
  companyQuery = '',
  allowedOrgIds = null,
}) {
  const nodes = [{ id: 'you', kind: 'center', label: centerLabel || 'YOU', color: '#34D399', size: 24, fx: 0, fy: 0 }];
  const links = [];

  const anchors = [];
  workAnchors.forEach((name) => anchors.push({ id: `work:${name}`, kind: 'anchor_work', label: name, color: WORK_COLOR, size: 13 }));
  educationAnchors.forEach((name) => anchors.push({ id: `education:${name}`, kind: 'anchor_edu', label: name, color: EDU_COLOR, size: 13 }));

  const anchorRadius = 190;
  const anchorCount = Math.max(1, anchors.length);
  anchors.forEach((a, i) => {
    const angle = (-Math.PI / 2) + (i / anchorCount) * (2 * Math.PI);
    a.x = Math.cos(angle) * anchorRadius;
    a.y = Math.sin(angle) * anchorRadius;
    nodes.push(a);
    links.push({ source: 'you', target: a.id, kind: 'anchor' });
  });

  if (!selectedAnchor || selectedAnchor === 'you') return { nodes, links };

  let rows = [];
  let scope = 'work';
  if (selectedAnchor.startsWith('work:')) {
    const key = selectedAnchor.slice(5);
    rows = Array.isArray(workTabsData?.[key]) ? workTabsData[key] : [];
  } else if (selectedAnchor.startsWith('education:')) {
    const key = selectedAnchor.slice(10);
    rows = Array.isArray(collegeTabsData?.[key]) ? collegeTabsData[key] : [];
    scope = 'education';
  }

  const q = norm(companyQuery);
  const base = !q
    ? rows
    : rows.filter((c) => {
        const org = norm(c?.organization);
        return org.includes(q);
      });

  const hasRoleFilter = allowedOrgIds instanceof Set;
  const byTier = { T1: [], T2: [], T3: [], T4: [], T5: [], T6: [] };
  base.forEach((c) => {
    if (hasRoleFilter) {
      const orgId = Number(c?.organization_id ?? c?.dest_org_id ?? c?.org_id ?? c?.organizationId ?? c?.orgId);
      if (!Number.isFinite(orgId) || !allowedOrgIds.has(orgId)) return;
    }
    const tier = classifyTier(getLabels(c), scope);
    byTier[tier].push(c);
  });

  const selectedAnchorNode = anchors.find((a) => a.id === selectedAnchor);
  const anchorX = typeof selectedAnchorNode?.x === 'number' ? selectedAnchorNode.x : 0;
  const anchorY = typeof selectedAnchorNode?.y === 'number' ? selectedAnchorNode.y : -anchorRadius;
  const tierRadius = 165;

  TIERS.forEach((tier, idx) => {
    const count = byTier[tier]?.length || 0;
    if (!count) return;
    const tierLabelMap = scope === 'work' ? WORK_TIER_LABELS : EDU_TIER_LABELS;
    const tierPalette = scope === 'work' ? WORK_TIER_COLORS : EDU_TIER_COLORS;
    const tierId = `tier:${tier}`;
    const theta = (-Math.PI * 0.8) + (idx / Math.max(1, TIERS.length - 1)) * (Math.PI * 1.6);
    const tx = anchorX + Math.cos(theta) * tierRadius;
    const ty = anchorY + Math.sin(theta) * tierRadius;
    nodes.push({
      id: tierId,
      kind: 'tier',
      label: tierLabelMap[tier] || tier,
      size: 14,
      color: tierPalette[idx] || (scope === 'work' ? '#10B981' : '#A78BFA'),
      tier,
      count,
      order: idx,
      x: tx,
      y: ty,
    });
    links.push({ source: selectedAnchor, target: tierId, kind: 'tier' });

    if (Array.isArray(selectedTiers) && selectedTiers.includes(tier)) {
      const tierRows = byTier[tier].slice(0, 40);
      const n = tierRows.length;
      const arc = Math.min(Math.PI * 1.8, Math.PI * 0.8 + n * 0.08);
      const start = -arc / 2;
      const step = n <= 1 ? 0 : (arc / (n - 1));
      const destRadius = 130 + Math.min(90, n * 1.2);
      tierRows.forEach((c, i) => {
        const id = `dest:${tier}:${c.organization_id || c.organization || i}`;
        const a = start + (i * step);
        const dx = tx + Math.cos(a) * destRadius;
        const dy = ty + Math.sin(a) * destRadius;
        nodes.push({
          id,
          kind: 'destination',
          label: c.organization || 'Unknown',
          color: scope === 'work' ? DEST_WORK_COLOR : DEST_EDU_COLOR,
          size: Math.min(8.5, 4 + Math.sqrt(Number(c?.count || 1))),
          data: c,
          x: dx,
          y: dy,
        });
        links.push({ source: tierId, target: id, kind: 'destination' });
      });
    }
  });

  return { nodes, links };
}

const NodeTooltip = ({ node, isDark, position, onFindPeople, onFindRoles }) => {
  if (!node || node.kind !== 'destination' || !node.data) return null;
  const c = node.data;

  const bg = isDark ? 'rgba(17,24,39,0.97)' : 'rgba(255,255,255,0.98)';
  const border = isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)';
  const txtMain = isDark ? '#fff' : '#0f172a';
  const txtMuted = isDark ? 'rgba(255,255,255,0.5)' : '#64748b';
  const accent = PEOPLE_ACCENT;
  const tags = getTagLabels(c);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.92 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.92 }}
      transition={{ duration: 0.15 }}
      style={{
        position: 'absolute',
        left: position.x + 16,
        top: position.y - 10,
        zIndex: 100,
        width: 280,
        padding: '16px 18px',
        borderRadius: 16,
        background: bg,
        border: `1px solid ${border}`,
        boxShadow: isDark ? '0 20px 50px rgba(0,0,0,0.6)' : '0 16px 40px rgba(0,0,0,0.12)',
        pointerEvents: 'auto',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 10,
            background: '#60A5FA20',
            border: '1.5px solid #60A5FA40',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 15,
            fontWeight: 800,
            color: '#93C5FD',
          }}
        >
          {(c.organization || '?')[0]}
        </div>
        <div>
          <p style={{ fontSize: 14, fontWeight: 700, color: txtMain, margin: 0 }}>{c.organization}</p>
          <p style={{ fontSize: 11, color: txtMuted, margin: 0 }}>{c.count || 0} contacts</p>
        </div>
      </div>

      {tags.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 10 }}>
          {tags.slice(0, 3).map((tag, i) => (
            <span
              key={i}
              style={{
                padding: '2px 8px',
                borderRadius: 999,
                fontSize: 10,
                fontWeight: 700,
                background: isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9',
                border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
                color: isDark ? 'rgba(255,255,255,0.6)' : '#475569',
              }}
            >
              {typeof tag === 'string' ? tag : tag?.label}
            </span>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 6, flexDirection: 'column' }}>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onFindPeople?.(c);
          }}
          style={{
            flex: 1,
            padding: '8px 0',
            borderRadius: 10,
            fontSize: 11,
            fontWeight: 700,
            background: accent,
            color: '#fff',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 5,
          }}
        >
          <Users size={12} /> Find my contact <ChevronRight size={11} />
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onFindRoles?.(c);
          }}
          style={{
            flex: 1,
            padding: '8px 0',
            borderRadius: 10,
            fontSize: 11,
            fontWeight: 700,
            background: isDark ? 'rgba(255,255,255,0.08)' : '#f1f5f9',
            border: isDark ? '1px solid rgba(255,255,255,0.12)' : '1px solid #e2e8f0',
            color: isDark ? 'rgba(255,255,255,0.8)' : '#334155',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 5,
          }}
        >
          <Briefcase size={12} /> Find open roles <ExternalLink size={10} />
        </button>
      </div>
    </motion.div>
  );
};

export default function ProfileGlobalMapView({
  workAnchors = [],
  educationAnchors = [],
  centerLabel = 'YOU',
  selectedAnchor = 'you',
  setSelectedAnchor,
  selectedTiers = [],
  setSelectedTiers,
  workTabsData = {},
  collegeTabsData = {},
  allowedOrgIds = null,
  isRoleFilterLoading = false,
  onDestinationClick,
  onDestinationAction,
}) {
  const { isDark } = useTheme();
  const fgRef = useRef(null);
  const wrapRef = useRef(null);
  const [dims, setDims] = useState({ w: 860, h: 520 });
  const [hoverNode, setHoverNode] = useState(null);
  const [activeNode, setActiveNode] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [companySearch, setCompanySearch] = useState('');
  const clickContextRef = useRef({ kind: 'none', id: null });

  const handleDestinationAction = useCallback(
    (company, action) => {
      if (typeof onDestinationAction === 'function') {
        onDestinationAction(company, action);
        return;
      }
      // Back-compat: old global map opened details immediately.
      if (typeof onDestinationClick === 'function') onDestinationClick(company);
    },
    [onDestinationAction, onDestinationClick]
  );

  const graph = useMemo(
    () =>
      buildGraph({
        workAnchors,
        educationAnchors,
        centerLabel,
        selectedAnchor,
        selectedTiers,
        workTabsData,
        collegeTabsData,
        companyQuery: companySearch,
        allowedOrgIds,
      }),
    [workAnchors, educationAnchors, centerLabel, selectedAnchor, selectedTiers, workTabsData, collegeTabsData, companySearch, allowedOrgIds]
  );

  useEffect(() => {
    if (!wrapRef.current) return;
    const ro = new ResizeObserver((entries) => {
      const r = entries[0].contentRect;
      setDims({ w: Math.max(360, r.width), h: Math.max(420, r.height) });
    });
    ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const fg = fgRef.current;
    if (!fg) return;
    fg.d3Force('charge')?.strength((n) => {
      if (n?.kind === 'destination') return -360;
      if (n?.kind === 'tier') return -260;
      if (n?.kind === 'anchor_work' || n?.kind === 'anchor_edu') return -240;
      return -300;
    });
    fg.d3Force('link')?.distance((l) => (l.kind === 'anchor' ? 165 : l.kind === 'tier' ? 128 : 112));
    fg.d3Force('link')?.strength((l) => (l.kind === 'anchor' ? 0.26 : l.kind === 'tier' ? 0.18 : 0.1));
    fg.d3Force('collide')?.radius((n) => {
      if (n?.kind === 'destination') return 18;
      if (n?.kind === 'tier') return 24;
      if (n?.kind === 'anchor_work' || n?.kind === 'anchor_edu') return 22;
      return 26;
    }).strength(0.95).iterations(2);
    fg.d3Force('center')?.strength(0.05);
    fg.d3AlphaDecay?.(0.045);
    fg.d3VelocityDecay?.(0.28);
  }, [graph]);

  // Smoothly refit only when anchor context changes.
  useEffect(() => {
    const fg = fgRef.current;
    if (!fg) return;
    const t = setTimeout(() => {
      try {
        fg.zoomToFit(750, 70);
      } catch (_) {
        // no-op
      }
    }, 180);
    return () => clearTimeout(t);
  }, [selectedAnchor]);

  const focusNode = useCallback((node, zoom = 1.55) => {
    const fg = fgRef.current;
    if (!fg || !node) return;
    if (typeof node.x !== 'number' || typeof node.y !== 'number') return;
    try {
      fg.centerAt(node.x, node.y, 520);
      const currentZoom = fg.zoom?.() || 1;
      fg.zoom(Math.max(currentZoom, zoom), 520);
    } catch (_) {
      // no-op
    }
  }, []);

  useEffect(() => {
    const fg = fgRef.current;
    if (!fg || selectedAnchor === 'you') return;
    const t = setTimeout(() => {
      try {
        fg.d3ReheatSimulation?.();
      } catch (_) {
        // no-op
      }
      const ctx = clickContextRef.current || {};
      const nodes = Array.isArray(graph?.nodes) ? graph.nodes : [];
      let nodeToFocus = null;
      if (ctx.kind === 'tier' && ctx.id) {
        nodeToFocus = nodes.find((n) => n.id === ctx.id) || null;
      } else if (ctx.kind === 'anchor' && ctx.id) {
        nodeToFocus = nodes.find((n) => n.id === ctx.id) || null;
      }
      if (nodeToFocus && typeof nodeToFocus.x === 'number' && typeof nodeToFocus.y === 'number') {
        focusNode(nodeToFocus, ctx.kind === 'tier' ? 1.5 : 1.4);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [graph, selectedAnchor, focusNode]);

  const handleNodeHover = useCallback(
    (node) => {
      setHoverNode(node || null);
      if (node && wrapRef.current) {
        const coords = fgRef.current?.graph2ScreenCoords?.(node.x, node.y);
        if (coords) {
          const rect = wrapRef.current.getBoundingClientRect();
          setTooltipPos({
            x: Math.min(coords.x, rect.width - 300),
            y: Math.min(coords.y, rect.height - 200),
          });
        }
      }
    },
    [wrapRef]
  );

  const handleNodeClick = useCallback(
    (node) => {
      if (node.kind === 'tier') {
        clickContextRef.current = { kind: 'tier', id: node.id };
        setSelectedTiers?.((prev) => {
          const arr = Array.isArray(prev) ? prev : [];
          return arr.includes(node.tier) ? arr.filter((t) => t !== node.tier) : [...arr, node.tier];
        });
        focusNode(node, 1.45);
        return;
      }
      if (node.id === 'you') {
        clickContextRef.current = { kind: 'center', id: 'you' };
        setSelectedAnchor?.('you');
        setSelectedTiers?.([]);
        setActiveNode(null);
        focusNode(node, 1.2);
        return;
      }
      if (node.kind === 'destination') {
        setActiveNode((prev) => (prev?.id === node.id ? null : node));
        // Keep global map behavior aligned with individual map:
        // click selects node; actions happen from tooltip buttons.
        if (wrapRef.current) {
          const coords = fgRef.current?.graph2ScreenCoords?.(node.x, node.y);
          if (coords) {
            const rect = wrapRef.current.getBoundingClientRect();
            setTooltipPos({
              x: Math.min(coords.x, rect.width - 300),
              y: Math.min(coords.y, rect.height - 200),
            });
          }
        }
        focusNode(node, 1.85);
        return;
      }

      // anchor nodes (work/education)
      clickContextRef.current = { kind: 'anchor', id: node.id };
      setSelectedAnchor?.((prev) => (prev === node.id ? 'you' : node.id));
      setSelectedTiers?.([]);
      setActiveNode(null);
      focusNode(node, 1.45);
    },
    [setSelectedAnchor, setSelectedTiers, focusNode]
  );

  const handleBgClick = useCallback(() => {
    setActiveNode(null);
  }, []);

  const tooltipNode = useMemo(() => {
    if (hoverNode?.kind === 'destination' && hoverNode?.data) return hoverNode;
    if (activeNode?.kind === 'destination' && activeNode?.data) return activeNode;
    return null;
  }, [hoverNode, activeNode]);

  const visibleTiers = useMemo(
    () => graph.nodes.filter((n) => n.kind === 'tier'),
    [graph.nodes]
  );

  const drawNode = (node, ctx, scale) => {
    const r = node.size || 8;
    const isSelectedAnchor = selectedAnchor === node.id;
    const isExpandedTier = node.kind === 'tier' && Array.isArray(selectedTiers) && selectedTiers.includes(node.tier);
    const isActive = activeNode?.id === node.id;
    const isHover = hoverNode?.id === node.id;

    ctx.beginPath();
    ctx.arc(node.x, node.y, r, 0, 2 * Math.PI);
    if (node.kind === 'center') {
      ctx.fillStyle = isDark ? '#111827' : '#0F172A';
      ctx.fill();
      ctx.lineWidth = 3;
      ctx.strokeStyle = '#34D399';
      ctx.stroke();
    } else if (node.kind === 'destination') {
      ctx.fillStyle = isDark ? `${node.color}22` : `${node.color}1A`;
      ctx.fill();
      ctx.lineWidth = isActive || isHover ? 3 : 2;
      ctx.strokeStyle = node.color;
      ctx.stroke();
    } else {
      ctx.fillStyle = isDark ? '#0B1325' : '#EEF2FF';
      ctx.fill();
      ctx.lineWidth = isSelectedAnchor || isExpandedTier || isActive || isHover ? 3 : 2;
      ctx.strokeStyle = node.color;
      ctx.stroke();
    }

    if (node.kind === 'tier') {
      ctx.fillStyle = node.color;
      ctx.font = `bold ${Math.max(10, 11 / Math.max(0.9, scale))}px Manrope, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(isExpandedTier ? '−' : '+', node.x, node.y);
    }

    if (node.kind !== 'center' && node.kind !== 'tier') {
      ctx.beginPath();
      ctx.arc(node.x + r * 0.55, node.y - r * 0.6, Math.max(2.2, r * 0.22), 0, 2 * Math.PI);
      ctx.fillStyle = '#F8FAFC';
      ctx.fill();
      ctx.lineWidth = 0.8;
      ctx.strokeStyle = '#CBD5E1';
      ctx.stroke();
    }

    const fontSize = node.kind === 'center' ? 14 : node.kind === 'tier' ? 11 : 10;
    ctx.font = `${node.kind === 'center' ? 800 : 600} ${fontSize / Math.max(0.9, scale)}px Manrope, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillStyle = node.kind === 'center'
      ? (isDark ? '#F8FAFC' : '#0F172A')
      : node.kind === 'anchor_edu'
        ? EDU_COLOR
        : node.kind === 'anchor_work'
          ? WORK_COLOR
          : node.color || '#93C5FD';

    const lbl = String(node.label || '');
    const max = node.kind === 'tier' ? 30 : 18;
    const txt = lbl.length > max ? `${lbl.slice(0, max - 1)}…` : lbl;
    ctx.fillText(txt, node.x, node.y + r + 5);

    if (node.kind === 'tier') {
      ctx.font = `${10 / Math.max(0.9, scale)}px Manrope, sans-serif`;
      ctx.fillStyle = isDark ? 'rgba(255,255,255,0.68)' : '#475569';
      ctx.fillText(`${node.count} companies`, node.x, node.y + r + 18);
    }
  };

  return (
    <div
      ref={wrapRef}
      style={{
        width: '100%',
        height: 520,
        borderRadius: 16,
        overflow: 'hidden',
        border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid #dbe6f7',
        background: isDark ? 'rgba(13,16,25,0.6)' : 'rgba(245,248,255,0.85)',
        position: 'relative',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 10,
          left: 12,
          zIndex: 4,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '8px 12px',
          borderRadius: 10,
          width: 220,
          background: isDark ? 'rgba(17,24,39,0.92)' : 'rgba(255,255,255,0.94)',
          border: isDark ? '1px solid rgba(255,255,255,0.12)' : '1px solid #dbe6f7',
          backdropFilter: 'blur(10px)',
        }}
      >
        <Search size={13} color={isDark ? 'rgba(255,255,255,0.45)' : '#94a3b8'} />
        <input
          value={companySearch}
          onChange={(e) => setCompanySearch(e.target.value)}
          placeholder="Search companies..."
          style={{
            border: 'none',
            outline: 'none',
            background: 'transparent',
            fontSize: 12,
            color: isDark ? '#fff' : '#1e293b',
            width: '100%',
            fontFamily: "'Inter',sans-serif",
          }}
        />
        {companySearch && (
          <button
            onClick={() => setCompanySearch('')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex' }}
          >
            <X size={12} color={isDark ? 'rgba(255,255,255,0.5)' : '#94a3b8'} />
          </button>
        )}
      </div>

      <div style={{
        position: 'absolute',
        top: 10,
        right: 12,
        zIndex: 4,
        display: 'flex',
        borderRadius: 10,
        overflow: 'hidden',
        border: isDark ? '1px solid rgba(255,255,255,0.12)' : '1px solid #dbe6f7',
        background: isDark ? 'rgba(17,24,39,0.92)' : 'rgba(255,255,255,0.94)',
      }}>
        {[
          { icon: <ZoomIn size={14} />, title: 'Zoom in', action: () => fgRef.current?.zoom((fgRef.current?.zoom?.() || 1) * 1.35, 280) },
          { icon: <ZoomOut size={14} />, title: 'Zoom out', action: () => fgRef.current?.zoom((fgRef.current?.zoom?.() || 1) / 1.35, 280) },
          { icon: <RotateCcw size={14} />, title: 'Reset view', action: () => fgRef.current?.zoomToFit(700, 80) },
        ].map((btn, idx) => (
          <button
            key={btn.title}
            title={btn.title}
            onClick={btn.action}
            style={{
              padding: '8px 10px',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              background: 'transparent',
              color: isDark ? 'rgba(255,255,255,0.75)' : '#475569',
              borderRight: idx < 2 ? (isDark ? '1px solid rgba(255,255,255,0.12)' : '1px solid #dbe6f7') : 'none',
            }}
          >
            {btn.icon}
          </button>
        ))}
      </div>
      {isRoleFilterLoading && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 5,
            background: isDark ? 'rgba(2,6,23,0.58)' : 'rgba(255,255,255,0.62)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 10,
              padding: '10px 14px',
              borderRadius: 12,
              background: isDark ? 'rgba(15,23,42,0.95)' : 'rgba(255,255,255,0.96)',
              border: isDark ? '1px solid rgba(255,255,255,0.16)' : '1px solid #dbe6f7',
              color: isDark ? 'rgba(255,255,255,0.9)' : '#334155',
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            <Loader2 size={15} className="animate-spin" />
            Applying company role filter...
          </div>
        </div>
      )}
      <ForceGraph2D
        ref={fgRef}
        graphData={graph}
        width={dims.w}
        height={dims.h}
        backgroundColor="rgba(0,0,0,0)"
        nodeRelSize={4}
        nodeCanvasObject={drawNode}
        nodeCanvasObjectMode={() => 'replace'}
        nodePointerAreaPaint={(node, color, ctx) => {
          ctx.fillStyle = color;
          ctx.beginPath();
          ctx.arc(node.x, node.y, (node.size || 8) + 8, 0, 2 * Math.PI);
          ctx.fill();
        }}
        linkColor={() => (isDark ? 'rgba(148,163,184,0.36)' : 'rgba(100,116,139,0.38)')}
        linkWidth={(l) => (l.kind === 'anchor' ? 1.6 : l.kind === 'tier' ? 1.25 : 1)}
        warmupTicks={25}
        cooldownTicks={180}
        minZoom={0.35}
        maxZoom={4}
        onNodeHover={handleNodeHover}
        onNodeClick={handleNodeClick}
        onBackgroundClick={handleBgClick}
        enableNodeDrag={true}
      />

      <AnimatePresence>
        {tooltipNode && (
          <NodeTooltip
            key={tooltipNode.id}
            node={tooltipNode}
            isDark={isDark}
            position={tooltipPos}
            onFindPeople={(c) => handleDestinationAction(c, 'people')}
            onFindRoles={(c) => handleDestinationAction(c, 'roles')}
          />
        )}
      </AnimatePresence>

      {selectedAnchor !== 'you' && visibleTiers.length > 0 && (
        <div
          style={{
            position: 'absolute',
            left: 12,
            bottom: 12,
            zIndex: 4,
            padding: '10px 12px',
            borderRadius: 12,
            maxWidth: 320,
            background: isDark ? 'rgba(17,24,39,0.92)' : 'rgba(255,255,255,0.94)',
            border: isDark ? '1px solid rgba(255,255,255,0.12)' : '1px solid #dbe6f7',
            backdropFilter: 'blur(10px)',
          }}
        >
          <div
            style={{
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: '0.08em',
              color: isDark ? 'rgba(255,255,255,0.45)' : '#64748b',
              marginBottom: 8,
              textTransform: 'uppercase',
            }}
          >
            Tiers
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {visibleTiers.map((tierNode) => {
              const active = Array.isArray(selectedTiers) && selectedTiers.includes(tierNode.tier);
              return (
                <button
                  key={tierNode.id}
                  onClick={() =>
                    setSelectedTiers?.((prev) => {
                      const arr = Array.isArray(prev) ? prev : [];
                      return arr.includes(tierNode.tier) ? arr.filter((t) => t !== tierNode.tier) : [...arr, tierNode.tier];
                    })
                  }
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    width: '100%',
                    textAlign: 'left',
                    padding: '5px 0',
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    color: active ? (isDark ? '#fff' : '#0f172a') : (isDark ? 'rgba(255,255,255,0.7)' : '#475569'),
                  }}
                  title={tierNode.label}
                >
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: tierNode.color,
                      boxShadow: active ? `0 0 0 3px ${tierNode.color}33` : 'none',
                    }}
                  />
                  <span style={{ flex: 1, fontSize: 11, fontWeight: 600, lineHeight: 1.3 }}>
                    {tierNode.label}
                  </span>
                  <span style={{ fontSize: 10, fontWeight: 700, color: tierNode.color }}>
                    {tierNode.count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

    </div>
  );
}
